import type { LinearRGB } from '../../core/color';
import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../mesh/attributes';
import { castleHalfWidth, RAIL_INSET } from './hullParts';
import { hullPoint, topAt, type FormedTemplate } from './shipForm';
import { timberPost } from './shipTimber';
import { clothStrip, type ClothSpan } from '../mesh/clothWind';

/*
 * The woodcut flies small square flags with a saltire from short staffs on the rails of its great
 * ships: at the head of the forecastle, on the waist rail forward of the main mast and on the poop
 * (woodcut 2739-2756 x 3180-3190, 2811-2830 x 3225-3233, 2915-2928 x 3195-3204; 3143 x 3177).
 * The painting draws none at a readable size, so they stand in the mid and near tiers alone
 * (docs/plans/ships.md 12). Measured on the woodcut's ship, 200 px from stem to stern: 0.045-0.085
 * of its length across and 0.035-0.05 deep. The aft orientation follows the woodcut; live cloth
 * ripples are an animation choice, with the staffs and hoists fixed.
 */
export const RAIL_FLAG = {
  /** Across and deep, as fractions of the ship's length. */
  width: 0.06,
  depth: 0.045,
  /** The staff's height above the rail, in flag depths. */
  staff: 1.6,
  staffSize: 0.07,
  thickness: 0.03,
  /** The saltire's arms: their depth as a fraction of the flag's, and how far each face stands off. */
  arm: 0.2,
  proud: 0.012,
} as const;

/* The poop flag stands near the taffrail, the waist flag 0.3 of the way from the main mast to the
   fore (the woodcut's pair: 0.36 of the length, the main mast 0.52), the bow flag 0.6 along the
   forecastle. */
const POOP_S = 0.95;
const WAIST_TOWARDS_FORE = 0.3;
const FORECASTLE_SHARE = 0.6;

export interface RailFlagStation {
  readonly s: number;
  readonly side: 1 | -1;
  /** The staff's foot on the rail, in the ship's frame. */
  readonly foot: Vec3;
}

export interface RailFlagColours {
  readonly field: LinearRGB;
  readonly saltire: LinearRGB;
  readonly staff: LinearRGB;
}

function waistStation(template: FormedTemplate): number {
  const square = template.masts.filter((m) => !m.lateen).map((m) => m.position);
  const [fore, main] = [Math.min(...square), Math.max(...square)];
  return main - WAIST_TOWARDS_FORE * (main - fore);
}

/** Where the staffs stand: a pair, one a side, at the forecastle, the waist and the poop. */
export function railFlagStations(template: FormedTemplate): readonly RailFlagStation[] {
  const at = [FORECASTLE_SHARE * template.form.forecastle.to, waistStation(template), POOP_S];
  return at.flatMap((s) =>
    ([1, -1] as const).map((side): RailFlagStation => {
      const x = hullPoint(template, s, 1)[0];
      const z = side * (castleHalfWidth(template, s) - RAIL_INSET);
      return { s, side, foot: [x, topAt(template, s), z] };
    })
  );
}

function style(color: LinearRGB, surface: SurfaceKind): VertexStyle {
  return { color, surface, edgeId: 0, seed: 0, strokeAngle: 0, extent: [0, 0] };
}

/* The field in the ship's centre plane, the hoist at the staff; each arm of the saltire runs
   between opposite corners, through the cloth, so it shows on both faces. */
function buildFlag(
  builder: MeshBuilder,
  foot: Vec3,
  size: readonly [number, number],
  colours: RailFlagColours
): void {
  const [w, d] = size;
  const [x, y, z] = foot;
  const top = y + RAIL_FLAG.staff * d;
  const half = RAIL_FLAG.thickness / 2;
  clothStrip(
    builder,
    style(colours.field, SURFACE.cloth),
    [x, top - d / 2, z],
    [x + w, top - d / 2, z],
    d,
    half * 2
  );
  const arm = RAIL_FLAG.arm * d;
  const across = RAIL_FLAG.thickness + 2 * RAIL_FLAG.proud;
  const cross = style(colours.saltire, SURFACE.cloth);
  const [low, high] = [top - d + arm / 2, top - arm / 2];
  clothStrip(builder, cross, [x, high, z], [x + w, low, z], arm, across);
  clothStrip(builder, cross, [x, low, z], [x + w, high, z], arm, across);
}

/** A flag with a saltire on a staff at every station, in the ship's frame (bow at -X). */
export function buildRailFlags(
  builder: MeshBuilder,
  template: FormedTemplate,
  colours: RailFlagColours
): readonly ClothSpan[] {
  const spans: ClothSpan[] = [];
  const size = [RAIL_FLAG.width * template.length, RAIL_FLAG.depth * template.length] as const;
  for (const { foot } of railFlagStations(template)) {
    const height = RAIL_FLAG.staff * size[1];
    timberPost(builder, style(colours.staff, SURFACE.wood), foot, height, RAIL_FLAG.staffSize);
    const start = builder.vertexCount;
    buildFlag(builder, foot, size, colours);
    spans.push({
      start,
      end: builder.vertexCount,
      anchor: foot,
      along: [1, 0, 0],
      normal: [0, 0, 1],
      length: size[0],
      amplitude: size[0] * 0.09,
      phase: foot[0] + foot[2],
    });
  }
  return spans;
}
